/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, {
  ReactElement,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { createAppThemeWithFontFaceDataURI } from "@screencloud/studio-custom-fonts";
import { App } from "../../../../store/apps/types";
import { PlayerState } from "../../../../store/rootReducer";
import styles from "./AppViewer.module.css";
import {
  AppTokenPayload,
  GenericMessage,
  InitializeMessagePayload,
  PMIMessageReceivedPayload,
} from "./types";
import { Theme } from "../../../../store/themes/types";
import { ScreenData, ScreenState } from "../../../../store/screen/types";
import {
  ConfigState,
  ContextConfig,
  DeviceConfig,
} from "../../../../store/config/types";
import { PlayerFile } from "../../../../store/files/types";
import { useManualQuery } from "graphql-hooks";
import { ConfigurationManager } from "../../../../configurationManager";
import { CREATE_APP_TOKEN } from "../../../../store/graphqlQueries";
import { DEFAULT_APP_INSTANCE_ID } from "../../../../constants";
import {
  makeConnectedMessage,
  makeInitializeMessage,
  makeStartMessage,
  makeTokenUpdatedMessage,
  sendMessage,
  getMediaProxyConfig,
  extractDurationMS,
} from "./utils";
import { Maybe } from "../../../../queries";
import { ContentFailureGenericCb } from "../TimelineViewer/types";
import {
  Logger,
  LogLevel,
  LogMessage,
  LogObjects,
} from "../../../../logger/logger";
import { AudioSettings } from "../../../../providers/AudioSettingsProvider/types";
import { AudioSettingsContext } from "../../../../providers/AudioSettingsProvider/AudioSettingsProvider";
import { ContentViewPulseItem } from "../../../../types/content";
import {
  finishPulseContent,
  requestPulseSuccess,
  setPlaybackDuration,
} from "../../../../store/pulse/actions";
import { GqlContentMeta } from "../../../../store/graphqlTypes";

export const log = new Logger("appViewer");
interface AppViewerProps {
  config: ConfigState;
  app?: App;
  contentItem?: ContentViewPulseItem;
  overrideAppInitialize?: Partial<InitializeMessagePayload>;
  theme?: Theme;
  screenData?: ScreenData;
  orgId?: string;
  screenId?: string;
  spaceId?: string;
  filesByAppInstanceId: {
    nodes: Array<PlayerFile>;
  };
  fullDurationMs: number;
  isPreload?: boolean;
  contextConfig: ContextConfig;
  isPreview: boolean;
  device?: DeviceConfig;
  initialPlaybackPositionMs: number;
  durationElapsedMs: number;
  featureFlags?: Maybe<string>[];
  onContentFailure: ContentFailureGenericCb;
  region: string;
  appViewerToken?: string;
  additionalContextKey?: string;
  zoneId?: string;
}

type RequestAuthTokenListener = ({
  data,
  requestId,
}: PMIMessageReceivedPayload) => void;

// The AppViewer is exported for reuse in the SiteViewerContainer.
// This will no longer need to be exported after Secure Sites has been refactored to use App Instances under the hood.
export const AppViewer = (
  props: AppViewerProps
): ReactElement<AppViewerProps> => {
  const {
    app,
    overrideAppInitialize,
    theme,
    screenData,
    orgId,
    screenId,
    spaceId,
    filesByAppInstanceId,
    fullDurationMs,
    contextConfig,
    isPreview,
    device,
    durationElapsedMs,
    featureFlags,
    region,
    onContentFailure,
    appViewerToken,
    additionalContextKey: additionalQrKey,
    config,
    contentItem,
    zoneId,
  } = props;

  // Helper function to check if this is pulse content
  const isPulseContent = contentItem && zoneId;
  // TODO - How does memo() interact with this ref? When does it reset?
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const viewerUrl =
    app?.viewerUrl ||
    overrideAppInitialize?.viewerUrl ||
    contentItem?.viewerUrl ||
    "";
  const isTesting = process.env.NODE_ENV === "test";

  const screen = useSelector<PlayerState, ScreenState>((state) => state.screen);
  const dispatch = useDispatch();

  const audioSettings = useContext<AudioSettings>(AudioSettingsContext);
  const [isAppInitialized, setIsAppInitialized] = useState(false);
  const [fetchAppToken] = useManualQuery<{
    createSignedRuntimeJwt: AppTokenPayload;
  }>(CREATE_APP_TOKEN, {
    useCache: false,
    skipCache: true,
    variables: {
      input: {
        name: app?.name || "",
        appId: app?.id || "",
        appInstanceId:
          overrideAppInitialize?.appInstanceId || DEFAULT_APP_INSTANCE_ID,
        screenId: screenId || "",
      },
    },
  });

  const requestAppToken = useCallback(async (): Promise<
    AppTokenPayload | undefined
  > => {
    try {
      const response = await fetchAppToken();
      if (response.data) {
        return (response.data as { createSignedRuntimeJwt: AppTokenPayload })
          .createSignedRuntimeJwt;
      }
      return undefined;
    } catch (e) {
      log.error({
        message: "fetchAppToken failed",
        context: {
          message: (e as Error)?.message,
        },
      });
      return undefined;
    }
  }, [fetchAppToken]);

  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      try {
        const { data, source } = event;
        // There may be multiple AppViewers on screen at once, e.g. multiple zones.
        if (!isTesting && source !== iframeRef.current?.contentWindow) {
          return;
        }

        // Only CONNECT, CONNECT_SUCCESS, DISCONNECT messages add this ___ thing.
        // The rest nest their data under a 2nd "data" key.
        // TODO - Remove this complexity.
        if (data.substr(0, 3) === "___") {
          const parsed = JSON.parse(data.substring(3));
          handleMessage(parsed);
        } else {
          const parsed = JSON.parse(data);
          handleMessage(parsed.data, parsed.requestId);
        }
      } catch (err) {
        log.warn({
          message: "Could not parse received postMessage",
          context: {
            err: (err as Error)?.message,
          },
          proofOfPlayFlag: true,
        });
      }
    };

    /**
     * 1 - Receive all messages from this app.
     */
    window.addEventListener("message", onMessage, false);
    /**
     * 2 - Deal with the messages.
     */
    const handleMessage = async (
      receivedMessage: GenericMessage,
      requestId?: number
    ): Promise<void> => {
      if (!receivedMessage) return;
      const replyMessages: GenericMessage[] = [];
      switch (receivedMessage.type) {
        case "CONNECT": {
          replyMessages.push(makeConnectedMessage());
          // send theme to check if bodyfont and headingfont is font url them return url as data uri
          const modifiedTheme = createAppThemeWithFontFaceDataURI(theme);
          if (app || contentItem) {
            replyMessages.push({
              ...makeInitializeMessage(
                app,
                region,
                filesByAppInstanceId,
                modifiedTheme,
                orgId,
                screenId,
                spaceId,
                screenData,
                fullDurationMs,
                contextConfig,
                device,
                durationElapsedMs,
                appViewerToken,
                additionalQrKey,
                screen.isMuted || audioSettings.shouldMuteMedia,
                screen.playerWidth!,
                screen.playerHeight!,
                screen.playerTimezone!,
                screen.timezoneOverride ?? "",
                config,
                contentItem
              ),
              requestId,
            });
            if (isPulseContent) {
              // Only dispatch if we have a contentItem and zoneId
              dispatch(
                requestPulseSuccess({
                  id: contentItem.id,
                  zoneId,
                  pulseItemKey: `${contentItem?.id}-${zoneId}`,
                  type: "initialize",
                  config: (contentItem.props as GqlContentMeta).config,
                  viewerUrl: contentItem.viewerUrl || "",
                  playbackWillFinishInMs: -1, // Will be updated when "finishing" event is received
                })
              );
            }
          } else if (overrideAppInitialize) {
            replyMessages.push({
              type: "initialize",
              payload: {
                ...overrideAppInitialize,
                context: {
                  ...contextConfig,
                  theme: modifiedTheme,
                  screenData,
                  appViewerToken,
                  session: {
                    id: additionalQrKey,
                  },
                  mediaProxyConfig: getMediaProxyConfig(config),
                },
                durationElapsedMs,
                featureFlags,
              },
              requestId,
            });
          }
          break;
        }
        case "initialized":
          setIsAppInitialized(true);
          break;
        case "started":
          // update to debug to reduce the amount of cost and a bit redundant with "Show App" event
          log.debug({
            message: `Player received app "started" confirmation for[app-${app?.name}]`,
            context: {
              name: app?.name,
              appid: app?.id,
              contentType: "app",
              isPreview: isPreview,
              isPreload: props.isPreload,
              hasAppViewerToken: !!appViewerToken,
              viewerUrl: viewerUrl,
            },
          });
          break;
        case "requestAuthToken":
          log.info({
            message: `Auth token requested for [app-${app?.name}]`,
            context: {
              appname: app?.name,
              contentType: "app",
              isPreview: isPreview,
              isPreload: props.isPreload,
            },
          });

          // if playing in a preview in editor apps
          // the request auth token has to come from studio as unsaved editor apps don't have an app instance yet
          if (isPreview) {
            ConfigurationManager.getInstance()
              .getRemoteApi()
              .remote.fire("requestAuthToken", { requestId });
          } else {
            const appTokenPayload = await requestAppToken();
            if (iframeRef.current) {
              replyMessages.push({
                type: "requestAuthToken",
                payload: { authToken: appTokenPayload?.signedRuntimeToken },
                requestId,
              });
            }
          }

          break;
        case "playerlog":
          if (receivedMessage?.payload?.message) {
            const logContext = receivedMessage?.payload?.context || {};
            const logMessage = `${receivedMessage?.payload?.message}`;
            const logObject: LogMessage | LogObjects = !receivedMessage?.payload
              ?.context
              ? logMessage
              : { message: logMessage, context: logContext };

            switch (receivedMessage?.payload?.level) {
              case LogLevel.Debug:
                log.debug(logObject);
                break;
              case LogLevel.Info:
                log.info(logObject);
                break;
              case LogLevel.Warning:
                log.warn(logObject);
                break;
              case LogLevel.Error:
                log.error(logObject);
                break;

              default:
                log.debug(logObject);
                break;
            }
          } else {
            console.warn(
              "Invalid Message Format, please check required params.",
              receivedMessage
            );
          }
          break;
        case "finishing":
          if (isPulseContent) {
            const durationMS = extractDurationMS(receivedMessage.payload);

            // Only dispatch if we have a valid number
            if (durationMS !== null && durationMS >= 0) {
              // Update pulse item with actual duration - this triggers DynamicViewer timeout reset
              log.info({
                message: `Finishing [app-${app?.name || contentItem?.type}]`,
                context: {
                  name: app?.name || contentItem?.type,
                  contentType: contentItem?.type || "app",
                  durationMS,
                },
              });
              dispatch(
                setPlaybackDuration({
                  pulseItemKey: `${contentItem?.id}-${zoneId}`,
                  durationMs: durationMS,
                })
              );
            }
          }
          break;
        case "finished":
          log.info({
            message: `Finished [app-${app?.name || contentItem?.type}]`,
            context: {
              name: app?.name || contentItem?.type,
              contentType: contentItem?.type || "app",
            },
          });
          if (receivedMessage.payload?.error) {
            onContentFailure();
          }
          if (isPulseContent) {
            // finishPulseContent will automatically trigger removePlaybackDuration via middleware
            dispatch(finishPulseContent(`${contentItem?.id}-${zoneId}`));
          }
          break;
      }

      replyMessages.forEach((message) => {
        if (iframeRef.current) {
          if (app || overrideAppInitialize || contentItem) {
            const { requestId, ...messageToSend } = message;
            try {
              sendMessage(
                iframeRef.current,
                messageToSend,
                viewerUrl,
                requestId
              );
            } catch (error) {
              /// do nothing
              console.warn("can not postMessage", error);
            }
          }
        }
      });
    };

    /**
     * Clean up when the App is removed from screen.
     */
    return (): void => {
      log.debug({
        message: `AppViewer - removeEventListener.`,
        context: {
          appId: app?.id,
          viewerUrl: app?.viewerUrl,
          name: app?.name,
          appInstallId: app?.appInstallId,
          contentType: "app",
          isPreview: isPreview,
          isPreload: props.isPreload,
        },
      });
      window.removeEventListener("message", onMessage, false);
    };
  }, [
    app,
    overrideAppInitialize,
    theme,
    screenData,
    screenId,
    orgId,
    filesByAppInstanceId,
    fullDurationMs,
    props.isPreload,
    viewerUrl,
    contextConfig,
    fetchAppToken,
    spaceId,
    isPreview,
    device,
    durationElapsedMs,
    featureFlags,
    region,
    isTesting,
    appViewerToken,
    additionalQrKey,
    screen.isMuted,
    audioSettings.shouldMuteMedia,
    screen.playerWidth,
    screen.playerHeight,
    screen.playerTimezone,
    screen.timezoneOverride,
    config,
    requestAppToken,
    dispatch,
    contentItem,
    zoneId,
    onContentFailure,
    isPulseContent,
  ]);

  useEffect(() => {
    // send the started message if the app changes from preload to current
    if (iframeRef.current && isAppInitialized && !props.isPreload) {
      sendMessage(iframeRef.current, makeStartMessage(), viewerUrl);
    }
  }, [props.isPreload, isAppInitialized, viewerUrl]);

  useEffect(() => {
    // send the new token down to the app
    if (appViewerToken && isAppInitialized && iframeRef.current) {
      sendMessage(
        iframeRef.current,
        makeTokenUpdatedMessage({ appViewerToken }),
        viewerUrl
      );
    }
  }, [appViewerToken, isAppInitialized, viewerUrl]);

  useEffect(() => {
    let listener: RequestAuthTokenListener | undefined = undefined;
    if (isPreview) {
      listener = ({ data, requestId }: PMIMessageReceivedPayload) => {
        if (iframeRef.current) {
          sendMessage(
            iframeRef.current,
            {
              type: "requestAuthToken",
              payload: data,
            },
            viewerUrl,
            requestId
          );
        }
      };

      ConfigurationManager.getInstance()
        .getRemoteApi()
        .remote.on("SP_SEND_REQUEST_AUTH_TOKEN", listener);
    }

    return () => {
      if (listener) {
        ConfigurationManager.getInstance()
          .getRemoteApi()
          .remote.off("SP_SEND_REQUEST_AUTH_TOKEN", listener);
      }
    };
  }, [viewerUrl, isPreview, config]);

  const onError = useCallback(
    (event: SyntheticEvent<HTMLIFrameElement>) => {
      // this is not of much use, as iframes of a different origin do not expose any meaningful details about errors.
      log.error({
        message: "AppViewer iframe error",
        context: {
          viewerUrl,
          appName: app?.name,
          appInstanceId: app?.id,
        },
      });
      onContentFailure();
    },
    [viewerUrl, app?.id, app?.name, onContentFailure]
  );

  /**
   * Cleanup
   */
  useEffect(() => {
    // returned function will be called on component unmount
    return () => {
      // note: to reduce the number of log in DataDog, this better to be tracked during development
      log.debug({
        message: `Unmount called for app.[app-${app?.name}]`,
        context: {
          name: app?.name,
          contentType: "app",
          isPreview: isPreview,
          isPreload: props.isPreload,
        },
      });
    };
    // Disable eslint because we want this to be called on unmount only
    // todo: solve the possible outdated information in the log line above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // TODO remove this hook and fix app  multiple rerender
    log.info({
      message: `Show App [app-${app?.name}]`,
      context: {
        viewUrl: app?.viewerUrl,
        name: app?.name,
        contentType: "app",
        isPreview: isPreview,
        isPreload: props.isPreload,
      },
    });
    // Ignore eslint because we want to send log lines only if app id or isPreload status change
    // todo: solve the possible outdated information problem in the log line above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.id, props.isPreload]);

  return (
    <>
      <iframe
        data-testid="app-viewer-iframe"
        ref={iframeRef}
        className={styles.iframe}
        title={app?.name || ""}
        src={viewerUrl}
        onError={onError}
      />
    </>
  );
};

AppViewer.displayName = "AppViewer";
